import { Zap, Smartphone, Monitor } from "lucide-react";

function VitalBadge({ value, thresholds, unit = "" }) {
  if (value == null) return <span className="text-xs text-slate-400">N/A</span>;
  const num = typeof value === "string" ? parseFloat(value) : value;
  let color, label;
  if (num <= thresholds[0]) { color = "#10b981"; label = "Good"; }
  else if (num <= thresholds[1]) { color = "#f59e0b"; label = "Fair"; }
  else { color = "#ef4444"; label = "Poor"; }
  return (
    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: `${color}15`, color }}>
      {label}
    </span>
  );
}

export default function WebsiteSpeedSection({ website_analysis, pagespeed }) {
  const ps = pagespeed || website_analysis?.pagespeed || {};
  const mobileScore = ps.performance;
  if (mobileScore == null) return null;

  const scoreColor = mobileScore >= 70 ? "#10b981" : mobileScore >= 40 ? "#f59e0b" : "#ef4444";
  const scoreLabel = mobileScore >= 90 ? "FAST" : mobileScore >= 70 ? "GOOD" : mobileScore >= 40 ? "FAIR" : "SLOW";

  // Parse LCP value in seconds
  const lcpMs = ps.lcp_ms;
  const lcpSec = lcpMs ? (lcpMs / 1000).toFixed(1) : null;
  // Parse TBT value in ms
  const tbtMs = ps.tbt_ms;
  const tbtVal = tbtMs != null ? Math.round(tbtMs) : null;
  // CLS value
  const clsVal = ps.cls_value != null ? ps.cls_value.toFixed(2) : null;

  return (
    <section id="website-speed" className="mb-6" data-testid="website-speed-section">
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 sm:px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center" style={{ background: "#f59e0b15" }}>
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
            </div>
            <div>
              <h3 className="font-bold text-sm sm:text-base text-[#0f172a]">Website Performance</h3>
              <p className="text-[10px] sm:text-xs text-slate-400">Core Web Vitals &amp; speed analysis</p>
            </div>
          </div>
          <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">LIVE DATA</span>
        </div>

        <div className="px-4 sm:px-6 py-5">
          {/* Speed scores */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="p-4 rounded-xl bg-slate-50 text-center">
              <Smartphone className="w-5 h-5 mx-auto mb-2 text-slate-500" />
              <p className="text-2xl font-bold" style={{ color: scoreColor }}>{mobileScore}</p>
              <p className="text-xs text-slate-400">Mobile Speed</p>
            </div>
            <div className="p-4 rounded-xl bg-slate-50 text-center">
              <Monitor className="w-5 h-5 mx-auto mb-2 text-slate-500" />
              <p className="text-2xl font-bold text-[#0f172a]">{ps.accessibility || "—"}</p>
              <p className="text-xs text-slate-400">Accessibility</p>
            </div>
          </div>

          {/* Core Web Vitals */}
          <h4 className="text-sm font-bold text-[#0f172a] mb-3">Core Web Vitals</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50">
              <div>
                <p className="text-sm font-medium text-[#334155]">LCP (Largest Contentful Paint)</p>
                <p className="text-xs text-slate-400">{ps.lcp_display || (lcpSec ? `${lcpSec} s` : "N/A")}</p>
              </div>
              <VitalBadge value={lcpSec} thresholds={[2.5, 4.0]} />
            </div>
            <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50">
              <div>
                <p className="text-sm font-medium text-[#334155]">TBT (Total Blocking Time)</p>
                <p className="text-xs text-slate-400">{ps.tbt_display || (tbtVal != null ? `${tbtVal} ms` : "N/A")}</p>
              </div>
              <VitalBadge value={tbtVal} thresholds={[200, 600]} />
            </div>
            <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50">
              <div>
                <p className="text-sm font-medium text-[#334155]">CLS (Cumulative Layout Shift)</p>
                <p className="text-xs text-slate-400">{ps.cls_display || (clsVal || "N/A")}</p>
              </div>
              <VitalBadge value={clsVal ? parseFloat(clsVal) : null} thresholds={[0.1, 0.25]} />
            </div>
          </div>

          {/* Extra stats */}
          {(ps.total_byte_weight || ps.network_requests || ps.tti_display) && (
            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-3 text-center">
              {ps.total_byte_weight && (
                <div>
                  <p className="text-sm font-bold text-[#0f172a]">{ps.total_byte_weight}</p>
                  <p className="text-[10px] text-slate-400">Page Size</p>
                </div>
              )}
              {ps.network_requests > 0 && (
                <div>
                  <p className="text-sm font-bold text-[#0f172a]">{ps.network_requests}</p>
                  <p className="text-[10px] text-slate-400">Requests</p>
                </div>
              )}
              {ps.tti_display && (
                <div>
                  <p className="text-sm font-bold text-[#0f172a]">{ps.tti_display}</p>
                  <p className="text-[10px] text-slate-400">Time to Interactive</p>
                </div>
              )}
            </div>
          )}

          {/* Score bar */}
          <div className="mt-6 pt-5 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[#0f172a]">Speed Score</span>
              <span className="text-sm font-bold" style={{ color: scoreColor }}>{mobileScore}/100 — {scoreLabel}</span>
            </div>
            <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${mobileScore}%`, background: scoreColor }} />
            </div>
            {mobileScore < 70 && (
              <p className="text-xs text-slate-500 mt-2">
                Mobile score is below 70. Slow mobile sites lose 53% of visitors before they convert.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
